import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, MaskLine, FadeIn, EASE } from "../components/Reveal";
import ClosingBlock from "../components/ClosingBlock";
import { CLINIC } from "../data/catalog";

const GROUPS = [
  {
    title: "About the products",
    faqs: [
      {
        q: "Who makes these?",
        a: "Dr. Shibina Shemim, an Ayurvedic physician with a BAMS. She formulates every product herself, and they are made under her own manufacturing licence — not by a contract manufacturer.",
      },
      {
        q: "Why only ten products?",
        a: "Because we make them ourselves, in small batches, in our own facility. Ten is what we can make properly. When ten feels stretched, we stop adding.",
      },
      {
        q: "Are they natural?",
        a: "They are made from natural ingredients, and the hair range is free of SLS and parabens. The full ingredient list is on every product page and every pack.",
      },
      {
        q: "Will these treat my condition?",
        a: "No. These are cosmetics, not medicines — they cleanse, soften and condition. If you are dealing with a specific condition, please consult a doctor, or see Dr. Shibina at the clinic.",
      },
      {
        q: "My skin is sensitive. Can I use these?",
        a: "Patch test first: a small amount on the inside of your forearm, left for 24 hours. Natural ingredients can still cause reactions. If you are unsure, message us before ordering.",
      },
    ],
  },
  {
    title: "Orders and delivery",
    faqs: [
      {
        q: "Do the products have an expiry date?",
        a: "Yes. Batch number, date of manufacture and best-before are printed on every pack. Batches are small, so what you receive is always recent.",
      },
      {
        q: "Where do you deliver?",
        a: "Across India. International shipping is being considered and is not available yet.",
      },
      {
        q: "How long will it take?",
        a: "We pack within two working days, and delivery takes three to seven working days after that. You get a tracking link by WhatsApp and email.",
      },
      {
        q: "How much is delivery?",
        a: "A flat charge applies to smaller orders, shown at checkout. Delivery is free on orders above ₹1,500.",
      },
      {
        q: "Can I pay cash on delivery?",
        a: "We are deciding this before launch. If offered, it will be above a minimum order value with a small handling fee stated up front.",
      },
      {
        q: "Can I collect from the clinic?",
        a: `Yes — from ${CLINIC.name}, Chavadimukku, Sreekaryam, during clinic hours. Mention it when you order and we will keep your parcel aside.`,
      },
      {
        q: "Do I need an account to order?",
        a: "No. Guest checkout works. If you sign in with your email — we send you a code, no password — your orders and addresses are saved for next time.",
      },
    ],
  },
  {
    title: "Returns and problems",
    faqs: [
      {
        q: "Can I return something?",
        a: "Unopened, sealed products can be returned within 7 days of delivery. Opened cosmetics cannot be taken back — that is a hygiene rule. Read the ingredient list and patch test before use.",
      },
      {
        q: "My order arrived damaged.",
        a: "Message us within 48 hours with photos of the parcel and the product, and we will replace it or refund you. Photograph the parcel before unpacking it fully.",
      },
      {
        q: "I received the wrong item.",
        a: "Tell us and we will send the right one, and collect the wrong one at our cost.",
      },
      {
        q: "The product didn't suit me.",
        a: "Tell us anyway. We cannot take back an opened product, but the feedback reaches the person who formulated it — and that makes the next batch better.",
      },
    ],
  },
];

const Item = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b hairline">
      <button onClick={() => setOpen(!open)} data-testid={testid} className="flex w-full items-center justify-between gap-6 py-6 text-left">
        <span className="font-serif text-lg font-light md:text-xl">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3, ease: EASE }} className="shrink-0 text-ink/50">
          <Plus className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="overflow-hidden">
            <p className="max-w-2xl pb-7 text-[15px] leading-relaxed text-ink/70">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FaqPage = () => (
  <div className="pt-36 md:pt-44" data-testid="faq-page">
    <header className="mx-auto max-w-7xl px-4 md:px-8">
      <FadeIn>
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-ink/30" />
          <p className="eyebrow">FAQs</p>
        </div>
      </FadeIn>
      <h1 className="mt-8 font-serif font-light leading-[1.05] text-[11vw] sm:text-6xl lg:text-7xl">
        <MaskLine delay={0.15}>The questions</MaskLine>
        <MaskLine delay={0.28}>
          <span className="italic text-sage-deep">everyone asks.</span>
        </MaskLine>
      </h1>
    </header>

    <section className="mx-auto max-w-4xl px-4 py-16 md:py-20">
      {GROUPS.map((g, gi) => (
        <Reveal key={g.title} className="mb-14">
          <h2 className="eyebrow" data-testid={`faq-group-${gi}`}>{g.title}</h2>
          <div className="mt-4 border-t hairline">
            {g.faqs.map((f, i) => (
              <Item key={f.q} q={f.q} a={f.a} testid={`faq-item-${gi}-${i}`} />
            ))}
          </div>
        </Reveal>
      ))}
    </section>

    <ClosingBlock />
  </div>
);

export default FaqPage;
